import { http } from '@/api/http';
import { decryptGatewayData } from '@/utils/paymentGatewayCrypto';

interface ApiEnvelope<T> {
  status: string;
  result: T;
}

// ── Custom product fields ─────────────────────────────────────────────────────

export interface CustomFieldValue {
  id: number;
  value: string;
  showValue?: string;
  price_amount: number;
  price_type: number; // 1=percentage, else=fixed amount
  label?: string;
  name?: string;
}

export interface CustomField {
  id: number;
  name: string;
  label: string;
  type?: string; // 'select', 'predefined_radio', etc.
  applicable_for?: number; // 1=both buy&rent, 2=rent only, 3=buy only
  is_private?: boolean;
  product_field_value: CustomFieldValue[];
}

export interface SelectedCustomField {
  id: number;
  value: string;
  name: string;
  label: string;
  amount: number;
  type: number;
  applicable_for?: number;
  // Booking product option extras (sent verbatim to the cart API)
  showValue?: string;
  price?: number;
  price_amount?: number;
  price_type?: number;
  selectedValue?: number;
  quantity?: number;
}

// ── Exact-time / booking products ─────────────────────────────────────────────

export interface ExactDuration {
  value: string;          // e.g. "1", "2"
  type: string;           // e.g. "day", "hour"
  id: number;
  label?: string;
  times: string[];        // e.g. ["09:00 AM", "10:00 AM"]
  values: { id: number; value: string }[]; // maps time string → exact_times_id
}

export interface ExactTimeResult {
  durations: ExactDuration[];
  times: string[];
}

// ── Cart / order payloads ─────────────────────────────────────────────────────

export interface CartItemPayload {
  // Optional: CLI only sends this once a cart genuinely exists server-side — omitted (not 0)
  // on the very first add of a session, letting the server create the cart and return its
  // own token. See useCartSession's hasServerCart.
  token?: number;
  product_id: number;
  // CLI sends this as a formatted decimal string ("20.00"), not a raw number.
  price: number | string;
  price_id: number;
  quantity: number;
  rent_start: string;
  rent_end: string;
  rental_duration: number;
  rental_type: string;
  term: number;
  sales_tax: number;
  deposit_amount: number;
  // CLI's POS product-details flow (cash_register) never sets is_admin or zone on the cart
  // item at all — those only ever appear on the *package* add-to-cart payload, and
  // deposite_tax is only present when actually truthy (changeNullToEmpty strips it
  // otherwise). Keeping all three optional here so callers can genuinely omit them.
  deposite_tax?: boolean | string | number;
  variants_products_id: number;
  location: number;
  zone?: number;
  is_admin?: boolean;
  source?: string;
  customer_id?: number;
  custom_fields?: SelectedCustomField[];
  exact_times?: { id: number; date: string; time: string | null };
  booking?: boolean;
  add_anyway?: boolean;
  required_addons?: RequiredAddonPayloadItem[];
  driving_license_required?: boolean;
}

export interface AddToCartResult {
  store_hours_warning?: boolean;
  message?: string;
  /** The server's own cart token — CLI always re-syncs its stored token from this after
   *  every add-to-cart call (cash-register.service.ts addCart()), since the client-generated
   *  token is only provisional until the server actually creates a cart under it. */
  token?: number;
}

// ── Required add-ons ──────────────────────────────────────────────────────────

export interface AddonVariant {
  id: number;
  name: string;
  quantity_id: number;
  min_qty: number; // injected client-side; the quantity to send
}

export interface AddonProduct {
  id: number;
  name: string;
  image: string | null;
  min_quantity: number; // required units per 1 main item
  variants: AddonVariant[];
}

export interface RequiredAddonPayloadItem {
  product_id: number;
  variants_products_id: number;
  quantity_id: number;
  quantity: number;
}

export interface ApiCartItem {
  id: number;
  product_id: number;
  product_name?: string;
  name?: string;
  quantity: number;
  price: number;
  rent_start: string;
  rent_end: string;
  /** Same rental window as rent_start/rent_end but always "YYYY-MM-DD HH:mm" (24h) — rent_start/
   *  rent_end come back as "MM-DD-YYYY hh:mm AM/PM" from /products/availability, which callers
   *  expecting the parseable YYYY-MM-DD shape (formatDatetimeDisplay, the *_date-only comparisons
   *  elsewhere) must not read directly. Prefer these two when present. */
  rent_start_date?: string;
  rent_end_date?: string;
  deposit_amount?: number;
  sales_tax?: number;
  variants_products_id?: number;
  /** JSON-stringified array of already-assigned serialized assets, e.g. '[{"name":"SN-001"}]' —
   *  matches CLI's item.assets (cart.component.ts getAssetNameList()). Auto-assigned server-side;
   *  the cart page only ever displays these, never assigns them. */
  assets?: string | null;
  /** Per-item discount breakdown — an ad-hoc discount and a coupon's per-item share can both be
   *  present at once (CLI: cart.component.html isShowDiscount(item.discount), shown as two
   *  independent lines). */
  discount?: {
    off_amount?: number;
    discount_sub_total?: number | null;
    coupon_amount?: number;
    coupon_sub_total?: number | null;
  } | null;
}

export interface ApiCartTaxClass {
  name: string;
  rate: number;
  total: number;
}

export interface ApiCartTax {
  total: number;
  regular?: ApiCartTaxClass[];
}

export interface ApiCart {
  cart_id?: number;
  token?: number;
  total: string | number;
  sub_total?: number;
  tax?: ApiCartTax;
  additional_charge?: number;
  off_amount?: number;
  coupon_amount?: number;
  rent_start?: string;
  rent_end?: string;
  rent?: boolean;
  cart_items: ApiCartItem[];
}

// ── Cart item quantity update / removal (CLI: cart-qty.component.ts, cart.component.ts) ──

export interface UpdateCartQuantityPayload {
  id: number; // cart item id
  token: number;
  sales_tax: number;
  increment: 0 | 1; // matches CLI exactly: 1 = increase by 1, 0 = decrease by 1 (not a delta)
  price: number;
  rental_duration: number;
  option_id?: number;
  option_level?: 'parent' | 'child';
}

export async function updateCartItemQuantity(
  payload: UpdateCartQuantityPayload,
): Promise<ApiCart | null> {
  try {
    const raw = await http.post<ApiEnvelope<Record<string, unknown>>>('/carts/update', payload);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    return (r.data ?? r) as unknown as ApiCart;
  } catch {
    return null;
  }
}

export interface RemoveCartItemPayload {
  token: number;
  cart_id: number;
  cart_item_id: number;
  product_id: number;
}

export async function removeCartItem(payload: RemoveCartItemPayload): Promise<ApiCart | null> {
  try {
    const raw = await http.post<ApiEnvelope<Record<string, unknown>>>(
      '/carts/cart-remove-item',
      payload,
    );
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    return (r.data ?? r) as unknown as ApiCart;
  } catch {
    return null;
  }
}

export interface PaymentGatewayCard {
  id: number;
  name: string;
  is_online?: boolean;
  // CLI's own `activeGateway.status` — sent as-is in Transact's init payload (see
  // initTransactGateway()'s `status` field).
  status?: number;
  config?: { instructions?: string; [key: string]: unknown } | null;
}

export interface PaymentGatewayAtrium {
  id: number;
  name: string;
  config?: { instructions?: string; [key: string]: unknown } | null;
}

export interface PaymentGatewayAdditional {
  id: number;
  // 'cod' is the special-cased "cash" gateway (CLI shows it as a Cash method, not in the
  // additional/custom list); anything else is a genuine custom/offline "pay later"-style gateway.
  type: string;
  name: string;
  config?: {
    is_paid?: boolean;
    add_note?: boolean;
    instructions?: string;
    [key: string]: unknown;
  } | null;
}

export interface PaymentGatewaySwipe {
  id: number;
  // The real provider identifier — 'StripeCardPresent', 'FortisCardPresent',
  // 'FiservCardPresent', 'PAX', etc. Drives which swipe flow applies (Stripe's is the only one
  // with a "Sign & Pay" signature-capture variant; every provider also supports the generic
  // swipe-and-poll flow via /payments/swipe).
  name: string;
  online_type?: string;
  config?: { label?: string; [key: string]: unknown } | null;
}

export interface PaymentGatewayGroups {
  card?: PaymentGatewayCard | null;
  atrium?: PaymentGatewayAtrium | null;
  swipe?: PaymentGatewaySwipe | null;
  additional?: PaymentGatewayAdditional[];
}

/** A physical card-reader terminal assigned to this session (App.vue's location/terminal
 *  switcher), merged with its own `options` (device_type/terminal_reader/supports_signature) —
 *  see resolveTerminalConfig(). */
export interface TerminalConfig {
  id: number;
  name?: string;
  device_type?: string | null;
  terminal_provider?: string | null;
  terminal_reader?: string | null;
  supports_signature?: boolean;
  [key: string]: unknown;
}

/** CLI/POS: GET payments/gateways (plural) returns gateways grouped by category rather than a
 *  flat list. `card`/`atrium`/`additional` are handled directly by PaymentPanel.vue; `swipe`
 *  routes through a physical terminal via useStripeTerminal.ts / the /payments/swipe endpoint. */
export async function getPaymentGatewaysGrouped(): Promise<PaymentGatewayGroups> {
  type GR = { data?: PaymentGatewayGroups } | PaymentGatewayGroups;
  let raw: ApiEnvelope<GR>;
  try {
    // v2 (not the legacy v1 `/payments/gateways`) is mandatory here — matches rentmy_pos's
    // stores/payment.ts. v2 returns each gateway's `config` as AES-256-GCM ciphertext instead of
    // plain JSON, so gateway credentials (Stripe keys, etc.) never cross the wire — or sit in
    // localStorage — in the clear; decryptGatewayData() below undoes that client-side.
    raw = await http.get<ApiEnvelope<GR>>('/v2/payments/gateways?is_admin=1&source=pos&checkout=true');
  } catch {
    return {};
  }
  if (raw.status !== 'OK') return {};
  const r = raw.result as ({ data?: PaymentGatewayGroups } & PaymentGatewayGroups) | undefined;
  const groups = r?.data ?? r ?? {};
  // No silent fallback: v2's `config` always arrives encrypted, so a missing/invalid decryption
  // key is a real configuration problem, not "no gateways configured" — let it throw so the
  // caller can surface a visible error instead of quietly hiding a working gateway.
  const { storeId } = getAuthMeta();
  return decryptGatewayData(groups, storeId || null);
}

export interface CreateOrderPayload {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  company?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  token: number;
  // CLI's real order `type`: 1 = card/online gateway, 2 = cash, 3 = additional/custom gateway.
  type: number;
  is_admin: boolean;
  order_source: string;
  currency: string;
  delivery: { charge: number; tax: number; type: string };
  shipping_method: number;
  // Cash sends neither of these — there's no gateway involved for it.
  payment_gateway_name?: string;
  gateway_id?: number;
  // CLI: for Stripe this is the raw payments/stripe-intent response data object (not a token
  // string) — the charge already happened server-side by the time this is sent.
  account?: string | Record<string, unknown>;
  name_on_card?: string;
  capture?: boolean;
  // Card-present (swipe/terminal) payments: response_text is the raw terminal response (a
  // Stripe payment_intent for the Sign & Pay flow, or whatever /payments/swipe returned for the
  // generic swipe flow); terminal is the physical reader's HSN; swipe:true flags the order as
  // card-present so the backend doesn't also expect a stripe-intent-style `account`.
  response_text?: unknown;
  terminal?: string;
  swipe?: boolean;
  payment_amount?: number;
  due_amount?: number;
  amount_tendered?: number;
  // Only sent alongside an additional/custom-gateway payment, matching CLI.
  note?: string;
  salesman?: number;
  location: number;
  pickup?: number | null;
  return_to?: number | null;
  special_instructions?: string;
  special_requests?: string;
  custom_values: unknown[];
  terms_and_condition_accepted: boolean;
}

export interface CreateOrderResponse {
  success: boolean;
  order_id: number;
  message?: string;
}

// ── Utility ───────────────────────────────────────────────────────────────────

export function generateCartToken(): number {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return (arr[0] % 900000000) + 100000000;
}

export function getAuthMeta(): {
  locationId: number; userId: number; userTypeId: number; storeId: number; terminalId: number | null;
} {
  try {
    const raw = localStorage.getItem('auth_user');
    if (raw) {
      const p = JSON.parse(raw) as {
        location_id?: string | number;
        user_id?: number;
        id?: number;
        user_type_id?: number;
        store_id?: string | number;
        terminal_id?: number | null;
      };
      return {
        locationId: p.location_id != null ? Number(p.location_id) : 0,
        userId: p.user_id ?? p.id ?? 0,
        userTypeId: p.user_type_id ?? 0,
        storeId: p.store_id != null ? Number(p.store_id) : 0,
        // Set by App.vue's location/terminal switcher — a physical card-reader terminal
        // assigned to this session, required for swipe payments (see stripeCollectInputs()).
        terminalId: typeof p.terminal_id === 'number' ? p.terminal_id : null,
      };
    }
  } catch { /* */ }
  return { locationId: 0, userId: 0, userTypeId: 0, storeId: 0, terminalId: null };
}

/** Reads the terminal record + its (JSON-string-or-object) `options` merged on top — matches
 *  rentmy_pos's stores/payment.ts detectTerminal(). Locations are cached in localStorage by
 *  cacheLocations() (src/api/locations.ts) whenever the location list is loaded. */
export function resolveTerminalConfig(terminalId: number | null): TerminalConfig | null {
  if (terminalId == null) return null;
  try {
    const locations = JSON.parse(localStorage.getItem('locations') ?? '[]') as Array<{
      stores_terminals?: Array<Record<string, unknown>>;
    }>;
    for (const loc of locations) {
      const term = loc.stores_terminals?.find((t) => t.id === terminalId);
      if (!term) continue;
      const { options, ...rest } = term;
      if (typeof options === 'string' && options) {
        try {
          return { ...rest, ...(JSON.parse(options) as Record<string, unknown>) } as TerminalConfig;
        } catch {
          return rest as TerminalConfig;
        }
      }
      if (options && typeof options === 'object') {
        return { ...rest, ...(options as Record<string, unknown>) } as TerminalConfig;
      }
      return rest as TerminalConfig;
    }
  } catch { /* */ }
  return null;
}

// ── PIN authorization for discounts (CLI: AuthorizedPinComponent / users/authorize-pin) ──
// CLI only requires this for user_type_id === 4 ("Warehouse"/cashier role); other roles
// skip straight to the discount panel.

export async function authorizePin(
  pinNumber: number,
  userId: number,
): Promise<{ success: boolean; message?: string }> {
  try {
    const raw = await http.post<ApiEnvelope<Record<string, unknown>>>('/users/authorize-pin', {
      pin_number: pinNumber,
      user_id: userId,
    });
    const data = raw.result?.data as { success?: boolean; message?: string } | undefined;
    return { success: !!data?.success, message: data?.message };
  } catch {
    return { success: false, message: 'Something went wrong. Please try again.' };
  }
}

// ── Ad-hoc per-cart-item discount (CLI: carts/add-discount) ──────────────────────

export interface CartItemDiscountPayload {
  token: number;
  cart_item_id: number;
  sub_total: number; // the final discounted price, i.e. basePrice - off_amount
  off_amount: number; // percent (10/15/20 preset) — CLI's naming, not a dollar amount
  discount_amount: number; // the dollar amount actually taken off
  note: string;
}

export async function addCartItemDiscount(
  payload: CartItemDiscountPayload,
): Promise<{ cart?: ApiCart; message?: string; error?: string }> {
  try {
    const raw = await http.post<ApiEnvelope<Record<string, unknown>>>(
      '/carts/add-discount',
      payload,
    );
    // CLI reads `res.result.data.message` for BOTH success and failure — a "NOK"/errored
    // response still carries the real reason there, so surface it instead of swallowing it
    // into a generic "failed" message.
    const data = raw.result?.data as { cart?: ApiCart; message?: string } | undefined;
    if (raw.status !== 'OK' || !data?.cart) {
      return { error: data?.message ?? `Discount request failed (status: ${raw.status})` };
    }
    return { cart: data.cart, message: data.message };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Network error' };
  }
}

// ── Variant product (price source) ────────────────────────────────────────────

export interface VariantProductPriceData {
  prices?: unknown[];            // nested group format: [{ base:{}, hourly:[...], daily:[...] }]
  price?: import('@/api/inventory').ApiPriceItem[];
  rental_prices?: import('@/api/inventory').ApiPriceItem[];
  deposit_amount?: number;
  deposite_tax?: number | string | boolean;
  sales_tax?: number;
  product_quantity?: number;
  /** Real-time available quantity from the variant-product endpoint */
  available?: number;
  hourly_status?: boolean;
  booking?: boolean;
  exact_time?: boolean;
  extact_durations?: unknown;
}

export async function getVariantProductPrices(
  variantProductId: number,
  opts?: { startDate?: string; endDate?: string; token?: number },
): Promise<VariantProductPriceData | null> {
  try {
    type VPResult = Record<string, unknown>;
    const { locationId } = getAuthMeta();
    const today = new Date().toISOString().slice(0, 10);
    const startDate = opts?.startDate ?? today;
    const endDate   = opts?.endDate   ?? today;
    const q = new URLSearchParams({
      source: 'pos', type: 'cart',
      location: String(locationId),
      start_date: startDate,
      end_date: endDate,
      ...(opts?.token ? { token: String(opts.token) } : {}),
    });
    const raw = await http.get<ApiEnvelope<VPResult>>(`/products/view/variant-product/${variantProductId}?${q}`);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    // API may return { data: {...fields}, prices: [...] } — merge so prices is never lost
    if (r.data && typeof r.data === 'object') {
      return { ...(r.data as Record<string, unknown>), ...r } as VariantProductPriceData;
    }
    return r as unknown as VariantProductPriceData;
  } catch {
    return null;
  }
}

// ── POS product detail (CLI: ProductResolver.getProducts → products/{id}/user/details/{tz}) ──
// This is the endpoint CLI's product-details page actually loads from — unlike the plain
// /products/{id} REST endpoint, it's location/date/cart-aware and is the one that returns
// variant_set_list, variant_list, and default_variant for a multi-variant product. Without
// it, variant dropdowns (Size/Type/etc.) never appear and pricing can't resolve.

export interface PosProductVariantSet {
  id: number;
  name: string;
}
export interface PosProductVariantListItem {
  id: number;
  name: string;
  variant_set_id: number;
  /** True for the option that makes up the product's default variant combo. */
  selected?: boolean;
}
export interface PosProductDefaultVariant {
  variants_products_id: number;
  [key: string]: unknown;
}

export interface PosProductDetail {
  id: number;
  type: number;
  uuid?: string;
  name: string;
  url?: string;
  status?: number;
  image?: string;
  barcode?: string;
  client_specific_id?: string;
  store_id?: number | null;
  product_quantity?: number;
  variant_set_list?: PosProductVariantSet[];
  variant_list?: PosProductVariantListItem[];
  default_variant?: PosProductDefaultVariant;
  variants_products_id?: number;
  prices?: unknown[];
  price?: import('@/api/inventory').ApiPriceItem[];
  available?: number;
  available_for_sale?: number;
  deposit_amount?: number;
  deposite_tax?: number | string | boolean;
  sales_tax?: number;
  booking?: boolean;
  exact_time?: boolean;
  exact_date?: boolean;
  rent_start?: string;
  rent_end?: string;
  rental_price?: number;
  driving_license?: boolean;
  [key: string]: unknown;
}

export async function getPosProductDetail(
  productUuid: string,
  opts?: {
    assetId?: string;
    startDate?: string;
    endDate?: string;
    defaultVariantId?: number;
    token?: number;
  },
): Promise<PosProductDetail | null> {
  try {
    const { locationId } = getAuthMeta();
    const offset = new Date().getTimezoneOffset();
    const tzOffset = offset === 0 ? 0 : -offset;
    // CLI's route param that looks like ":id" is actually bound to the product's UUID
    // (confirmed via its own URL bar: .../product-details/{uuid}/{slug}) — this endpoint
    // 404s ("Not Found") if given the plain numeric product id instead.
    const q = new URLSearchParams({ location: String(locationId) });
    if (opts?.token) q.set('token', String(opts.token));
    if (opts?.assetId) q.set('asset_id', opts.assetId);
    if (opts?.startDate) q.set('start_date', opts.startDate);
    if (opts?.endDate) q.set('end_date', opts.endDate);
    if (opts?.defaultVariantId) q.set('default_variant', String(opts.defaultVariantId));
    const raw = await http.get<ApiEnvelope<{ data: PosProductDetail }>>(
      `/products/${productUuid}/user/details/${tzOffset}?${q}`,
    );
    if (raw.status !== 'OK') return null;
    return raw.result?.data ?? null;
  } catch {
    return null;
  }
}


// ── API functions ─────────────────────────────────────────────────────────────

export async function getRequiredAddons(productId: number, locationId: number): Promise<{ label: string; addons: AddonProduct[] }> {
  try {
    type AResult = { data?: AddonProduct[]; label?: string };
    const raw = await http.get<ApiEnvelope<AResult>>(`/products/${productId}/addons?required=true&location=${locationId}`);
    if (raw.status !== 'OK') return { label: '', addons: [] };
    const result = raw.result;
    const addons: AddonProduct[] = (result?.data ?? []).map(p => ({
      ...p,
      variants: (p.variants ?? []).map((v, idx) => ({
        ...v,
        min_qty: idx === 0 ? p.min_quantity : 0,
      })),
    }));
    return { label: result?.label ?? 'Required Add-ons', addons };
  } catch {
    return { label: '', addons: [] };
  }
}

export async function getProductCustomFields(productId: number): Promise<CustomField[]> {
  try {
    type CFResult = { data?: CustomField[] } | CustomField[];
    const raw = await http.get<ApiEnvelope<CFResult>>(`/products/custom-fields/values/${productId}`);
    if (raw.status !== 'OK') return [];
    const r = raw.result;
    if (Array.isArray(r)) return r;
    const withData = r as { data?: CustomField[] };
    return Array.isArray(withData.data) ? withData.data : [];
  } catch {
    return [];
  }
}

export async function getProductExactTime(productId: number, startDate: string): Promise<ExactTimeResult | null> {
  try {
    type ETResult = { data?: ExactTimeResult } & Partial<ExactTimeResult>;
    const raw = await http.get<ApiEnvelope<ETResult>>(`/product/${productId}/get-exact-time?start_date=${startDate}`);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    if (r.data?.durations) return r.data;
    if (r.durations) return r as ExactTimeResult;
    return null;
  } catch {
    return null;
  }
}

// ── Ad-hoc "add new time slot" for a booking product with 0-1 existing slots ──
// (CLI: product-details-booking.component.ts addNewEstItem()/exactDateTimeDataForamt(),
// POST exact-datetime). Only the fields CLI's ExactDateTime model actually varies per-call
// are exposed here; the rest match its class defaults exactly.
export interface CreateExactDateTimePayload {
  name: string;
  time: string; // 24h "HH:mm"
  duration_value: string; // hours, as a numeric string — matches CLI's duration_values[0]
  day: number; // 0=Sunday..6=Saturday, matches CLI's getDayWeekIndex()
  productId: number;
}

export async function createExactDateTime(
  payload: CreateExactDateTimePayload,
): Promise<{ id: number; time: string } | null> {
  try {
    const body = {
      name: payload.name,
      time: payload.time,
      content_type: 1,
      date: '',
      days: [payload.day],
      duration_type: 'hour',
      duration_values: [payload.duration_value],
      content_id: [],
      exclude_dates: [],
      exception_deleted_ids: [],
      start_date: '',
      end_date: '',
      color: '',
      products: [payload.productId],
    };
    const raw = await http.post<ApiEnvelope<Record<string, unknown>>>('/exact-datetime', body);
    if (raw.status !== 'OK') return null;
    const data = raw.result?.data as { id?: number; time?: string } | undefined;
    if (data?.id == null) return null;
    return { id: data.id, time: data.time ?? payload.time };
  } catch {
    return null;
  }
}

export interface DurationDateResult {
  start_date: string;
  end_date: string;
  price?: number;
  available?: number;
}

export async function getDatesFromDuration(payload: {
  product_id: number;
  variants_products_id: number;
  start_date: string;
  start_time: string | null;
  duration: number | string;
  type: string;
  exact_times_id: number;
  location_id: number;
}): Promise<DurationDateResult | null> {
  try {
    type DR = { data?: DurationDateResult };
    const raw = await http.post<ApiEnvelope<DR>>('/product/get_dates_from_duration', payload);
    if (raw.status !== 'OK') return null;
    return raw.result.data ?? null;
  } catch (e) {
    console.error('[getDatesFromDuration] error:', e);
    return null;
  }
}

export interface PriceValuePayload {
  product_id: number;
  variants_products_id: number;
  quantity: number;
  price_id: number;
  rental_type: string;
  rental_duration: number;
  term: number;
  rent_start: string;
  rent_end: string;
  price: number;
  location: number;
  custom_fields: SelectedCustomField[];
  current_time: string;
}

export async function getPriceValue(
  payload: PriceValuePayload,
): Promise<{ price: number; available?: number; start_date?: string; end_date?: string; message?: string } | null> {
  try {
    // CLI reads: result.data = price, result.available = qty, result.start_date / result.end_date.
    // `message` is set (e.g. "Rental time(s) were adjusted for store open hours") when the server
    // clamped the requested rent_start/rent_end to fit the location's open hours — start_date/
    // end_date then hold the corrected values, not just an echo of what was sent.
    // `data` is a decimal STRING in practice (e.g. "225.00"), not a number — confirmed via live
    // network capture. The old number-or-object parsing silently zeroed the price on every real
    // response (string didn't match either branch, falling through to `?? 0`), which is why
    // changing an exact-time product's duration/start time never updated the shown total.
    type PVR = { data?: number | string | { price?: number }; available?: number; start_date?: string; end_date?: string; message?: string; error?: string };
    const raw = await http.post<ApiEnvelope<PVR>>('/get-price-value', payload);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    if (r.error) return null;
    const d = r.data;
    const price = typeof d === 'number' ? d
      : typeof d === 'string' ? Number(d)
      : ((d as { price?: number })?.price ?? 0);
    return { price, available: r.available, start_date: r.start_date, end_date: r.end_date, message: r.message };
  } catch {
    return null;
  }
}

export async function getProductDateRange(
  startDate: string,
  priceId: number,
  locationId: number,
): Promise<{ start_date: string; end_date: string } | null> {
  try {
    const now = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    // CLI's getCurrentDateTime() (functions.ts) formats as "YYYY-MM-DD HH:mm" — no seconds,
    // space-separated. start_date must be sent the same way (our own refs are "T"-separated
    // internally for the date picker, so convert here rather than at every call site).
    const currentTime = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}`;
    const q = new URLSearchParams({
      start_date: startDate.replace('T', ' '),
      price_id: String(priceId),
      location: String(locationId),
      current_time: currentTime,
    });
    type DR = { data?: { start_date: string; end_date: string }; start_date?: string; end_date?: string };
    const raw = await http.get<ApiEnvelope<DR>>(`/product/get_dates_price_duration?${q}`);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    if (r.data?.end_date) return r.data;
    if (r.end_date) return { start_date: r.start_date ?? startDate, end_date: r.end_date };
    return null;
  } catch {
    return null;
  }
}

// Matches CLI's changeNullToEmpty() exactly (globals/_classes/functions.ts) — every cart
// payload (add-to-cart, package add-to-cart) is run through this before being sent. It
// strips ANY falsy field, not just null/undefined — so 0, false, and "" fields are dropped
// entirely rather than sent as literal zero/false values. CLI's backend has therefore never
// actually seen e.g. `sales_tax: 0`, `deposit_amount: 0`, `zone: 0`, or
// `driving_license_required: false` on the wire; only ports that skip this step do.
function changeNullToEmpty<T extends object>(data: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key in data) {
    if (data[key]) out[key] = data[key];
  }
  return out;
}

export async function addToCart(item: CartItemPayload): Promise<AddToCartResult> {
  const raw = await http.post<ApiEnvelope<Record<string, unknown>>>(
    '/carts/add-to-cart',
    changeNullToEmpty(item),
  );
  // CLI explicitly guards on this: the backend can return HTTP-level status "OK" while the
  // save itself still failed (`result.error`, e.g. "Save failed") — cash-register.service.ts
  // addCart() only trusts the response when `!res.result.hasOwnProperty('error')`. Without
  // this check the app would silently show "added to cart" for an item that was never
  // actually saved server-side, leaving the local cart and the real cart out of sync.
  if (raw.status !== 'OK' || (raw.result && 'error' in raw.result)) {
    const errResult = raw.result as { message?: string; error?: string } | null;
    const msg = errResult?.message ?? errResult?.error ?? 'Failed to add item to cart';
    throw new Error(msg);
  }
  const r = raw.result as Record<string, unknown>;
  // Response may nest everything under `data` (CLI reads res.result.data.token/.cart_items)
  // or return it flat — handle both.
  const data = (r.data && typeof r.data === 'object' ? r.data : r) as Record<string, unknown>;
  return {
    store_hours_warning: !!(data?.store_hours_warning ?? r?.store_hours_warning),
    message: (data?.message as string | undefined) ?? (r?.message as string | undefined) ?? '',
    token: data?.token != null ? Number(data.token) : undefined,
  };
}

// CLI: cash_register/cash-register-service/cart.service.ts getCartList() always calls
// `carts/{token}?view_token={location_id}_{terminal_id}` (functions.ts getLocationAndTerminal())
// — a plain `carts/{token}` with no view_token fails server-side token+location validation with
// "Invalid cart token!" even when the token itself is exactly correct. getCartList() also accepts
// extra query params appended per-context (cart.component.ts's `loadCart(isUpdate, paramObj,
// isAfterSave)`): `action=add_service_charge` specifically when re-fetching right after saving
// additional charges, and `billing_country`/`billing_city`/`billing_state`/`billing_zipcode`
// (from getParam(), always sent — even empty — on the default post-add-to-cart reload) otherwise.
export async function getCart(token: number, extraParams?: Record<string, string>): Promise<ApiCart | null> {
  try {
    const { locationId, terminalId } = getAuthMeta();
    const viewToken = terminalId != null ? `${locationId}_${terminalId}` : String(locationId);
    const params = new URLSearchParams({ view_token: viewToken, ...extraParams });
    const raw = await http.get<ApiEnvelope<Record<string, unknown>>>(
      `/carts/${token}?${params}`,
    );
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    if (r.data && typeof r.data === 'object') return r.data as ApiCart;
    if (r.result && typeof r.result === 'object') return r.result as ApiCart;
    return r as unknown as ApiCart;
  } catch {
    return null;
  }
}

// ── Package products ─────────────────────────────────────────────────────────

export interface PackageProductVariant {
  id: number;
  variant_chain: string;
  available?: number;
  quantity?: number;
}

export interface PackageProduct {
  id: number;
  name: string;
  quantity: number;
  available?: number;
  max_quantity?: number;
  variants: PackageProductVariant[];
}

export interface PackageDetail {
  id: number;
  name: string;
  variants_products_id?: number;
  products: PackageProduct[];
  price?: unknown[];   // legacy flat array (may be empty)
  prices?: unknown[];  // nested grouped format: [{ base, daily: [...], weekly: [...] }]
  deposit_amount?: number;
  deposite_tax?: string | number | boolean;
  exact_time?: boolean;
  extact_durations?: { durations: ExactDuration[]; times: string[] };
  exact_date?: boolean;
  package_dynamic_bundle_builder?: boolean | number | string;
  package_max_item_count?: number;
  status?: number;
}

export interface PackageCartPayload {
  package_id: number;
  // Optional — same reasoning as CartItemPayload.token above.
  token?: number;
  price_id: number;
  location: number;
  rental_type: string;
  /** Auto-injected by addPackageToCart() below, matching CLI's packageAddCart() — no need
   *  for callers to set this themselves. */
  zone?: number;
  products: {
    variants_products_id: number;
    quantity: number;
    product_id: number;
    available: number;
    product_index?: number;
    is_selected?: boolean;
  }[];
  package_dynamic_bundle_builder?: boolean;
  variants_products_id: number;
  rental_duration: number;
  term: number;
  rent_start: string;
  rent_end: string;
  quantity: number;
  price: number | string;
  // CLI's packageAddCart() never sets is_admin either — only zone is injected server-side
  // (cash-register.service.ts:139-143).
  is_admin?: boolean;
  deposit_amount?: number;
  deposite_tax?: boolean | string | number;
  source?: string;
  customer_id?: number;
  custom_fields?: SelectedCustomField[];
  exact_times?: { id: number; date: string; time: string | null };
}

export async function getPackageDetail(uuid: string): Promise<PackageDetail | null> {
  try {
    const offset = new Date().getTimezoneOffset();
    const tzOffset = offset === 0 ? 0 : -offset;
    type PR = Record<string, unknown>;
    const raw = await http.get<ApiEnvelope<PR>>(`/package/${uuid}/details/${tzOffset}`);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    // Handle { result: { data: PackageDetail } } and { result: PackageDetail } shapes
    if (r.data && typeof r.data === 'object') return r.data as PackageDetail;
    if (r.id != null) return r as unknown as PackageDetail;
    return null;
  } catch (e) {
    console.error('[PackageDetail] error:', e);
    return null;
  }
}

export async function addPackageToCart(payload: PackageCartPayload): Promise<{ token?: number }> {
  const url = payload.rental_type === 'buy' ? '/package/add-to-cart/buy' : '/package/add-to-cart';
  // Matches CLI's packageAddCart(), which always injects this itself rather than relying on
  // the caller to set it.
  const offset = new Date().getTimezoneOffset();
  const zone = offset === 0 ? 0 : -offset;
  const raw = await http.post<ApiEnvelope<Record<string, unknown>>>(
    url,
    changeNullToEmpty({ ...payload, zone }),
  );
  if (raw.status !== 'OK' || (raw.result && 'error' in raw.result)) {
    const errResult = raw.result as { message?: string; error?: string; errors?: string } | null;
    const msg = errResult?.message ?? errResult?.error ?? errResult?.errors ?? 'Failed to add package to cart';
    throw new Error(msg);
  }
  const r = raw.result as Record<string, unknown>;
  const data = (r.data && typeof r.data === 'object' ? r.data : r) as Record<string, unknown>;
  return { token: data?.token != null ? Number(data.token) : undefined };
}

// ── Terms & conditions popup (CLI: modules/terms-condition/terms-condition.component.ts) ──

interface TermsConditionEntry {
  product_id?: number;
  product_name?: string;
  content?: string;
  parent?: boolean;
}

/** Fetches and concatenates the terms-and-conditions HTML for the given cart's products,
 *  exactly matching CLI's getTermsConditionwithIds(): parent/general entries first, each
 *  prefixed with a bold product-name heading when the entry has one. */
export async function getTermsAndConditions(productIds: number[]): Promise<string> {
  try {
    const ids = productIds.join(',');
    type TR = { data?: TermsConditionEntry[] };
    const raw = await http.get<ApiEnvelope<TR>>(`/terms-and-condition?product_ids=${ids}`);
    if (raw.status !== 'OK') return '';
    const items = raw.result?.data ?? [];
    return items
      .slice()
      .sort((a, b) => (!!a.parent === !!b.parent ? 0 : a.parent ? -1 : 1))
      .reduce((acc, item) => {
        const heading = item.product_name ? `<strong>${item.product_name}</strong><br>` : '';
        return acc + heading + (item.content ?? '');
      }, '');
  } catch {
    return '';
  }
}

// ── Custom checkout fields ────────────────────────────────────────────────────

export interface CustomCheckoutField {
  field_label: string;
  field_name: string;
  field_type: number | string; // 0=text, 1=select, 2=file, 3=textarea, 4=default/rich-text block
  field_values?: string;       // semicolon-separated options for type 1; static content for type 4
  field_is_required?: boolean;
  /** CLI: renders a signature pad under type 3/4 fields when this is set. */
  collect_signature?: boolean;
}

export async function getCustomCheckoutFields(
  cartId?: number,
  customerId?: number,
): Promise<CustomCheckoutField[]> {
  try {
    type COR = { data?: CustomCheckoutField[] };
    // Matches CLI's getAdditionalInfoBeforeOrder(): content_id/type scope the fetch to this
    // cart, and customer_id (only when a customer is already selected) lets the backend
    // pre-fill any values already on file for them.
    const q = new URLSearchParams({ section: 'checkout' });
    if (cartId) { q.set('content_id', String(cartId)); q.set('type', 'cart'); }
    if (customerId) q.set('customer_id', String(customerId));
    const raw = await http.get<ApiEnvelope<COR>>(`/custom-fields?${q}`);
    if (raw.status !== 'OK') return [];
    return raw.result?.data ?? [];
  } catch {
    return [];
  }
}

export interface MediaUploadResult {
  filename: string;
}

/** CLI: type-2 (file) custom-checkout fields upload immediately on selection via this endpoint,
 *  then substitute the returned filename into the field's value for final submission. */
export async function uploadCustomFieldFile(file: File, fieldName: string): Promise<string | null> {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('type', fieldName);
    type UR = { data?: MediaUploadResult } | MediaUploadResult;
    const raw = await http.upload<ApiEnvelope<UR>>('/media/upload', form);
    if (raw.status !== 'OK') return null;
    const r = raw.result as { data?: MediaUploadResult; filename?: string };
    return r?.data?.filename ?? r?.filename ?? null;
  } catch {
    return null;
  }
}

// ── Variant chain ─────────────────────────────────────────────────────────────

export interface VariantChainItem {
  id: number;
  name: string;
  variant_set_id: number;
}

export interface VariantChainEndResult {
  id?: number;
  variants_products_id?: number;
  available?: number;
  prices?: unknown[];
  images?: unknown[];
}

export async function getVariantChain(
  productId: number,
  variantId: number,
  variantChain: string,
  locationId: number,
  rentType: string,
): Promise<VariantChainItem[]> {
  try {
    const q = new URLSearchParams({
      product_id:    String(productId),
      variant_id:    String(variantId),
      variant_chain: variantChain,
      t:             String(Date.now()),
      location:      String(locationId),
      rent_type:     rentType,
    });
    type VCR = { data?: VariantChainItem[] } | VariantChainItem[];
    const raw = await http.get<ApiEnvelope<VCR>>(`/variant-chain?${q}`);
    if (raw.status !== 'OK') return [];
    const r = raw.result;
    if (Array.isArray(r)) return r;
    return (r as { data?: VariantChainItem[] }).data ?? [];
  } catch {
    return [];
  }
}

export async function getVariantChainEnd(
  productId: number,
  variantId: number,
  variantChain: string,
  locationId: number,
  rentType: string,
  token: number,
  rentStart = '',
  rentEnd   = '',
): Promise<VariantChainEndResult | null> {
  try {
    const tzOffset = -(new Date().getTimezoneOffset());
    const q = new URLSearchParams({
      product_id:    String(productId),
      variant_id:    String(variantId),
      variant_chain: variantChain,
      t:             String(tzOffset),
      location:      String(locationId),
      rent_type:     rentType,
      token:         String(token),
      rent_start:    rentStart,
      rent_end:      rentEnd,
    });
    type VCER = { data?: VariantChainEndResult } & Partial<VariantChainEndResult>;
    const raw = await http.get<ApiEnvelope<VCER>>(`/get-path-of-chain?${q}`);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    if (r.data) return r.data;
    if (r.id != null || r.variants_products_id != null) return r as VariantChainEndResult;
    return null;
  } catch {
    return null;
  }
}

// ── Coupon ────────────────────────────────────────────────────────────────────

export async function applyCoupon(
  token: number,
  code: string,
): Promise<{ cart: ApiCart }> {
  // Matches CLI's cart.component.ts applyCoupon() exactly — the endpoint is `carts/apply-coupon`
  // (not `carts/coupon`), the field is `coupon` (not `code`), and the response's `data` is the
  // *entire* refreshed cart summary (cartSummary = res.result.data), not a bespoke
  // {discount, message} shape — the coupon amount lives on `cart.coupon_amount`.
  type CR = { data?: ApiCart } | ApiCart;
  const raw = await http.post<ApiEnvelope<CR>>('/carts/apply-coupon', { token, coupon: code });
  if (raw.status !== 'OK') {
    const r = raw.result as { message?: string; error?: string } | null;
    throw new Error(r?.message ?? r?.error ?? 'Invalid coupon code');
  }
  const r = raw.result;
  const cart = (r as { data?: ApiCart })?.data ?? (r as ApiCart);
  if (!cart?.cart_items) throw new Error('Invalid coupon code');
  return { cart };
}

// ── Tax exempt ────────────────────────────────────────────────────────────────

export async function setTaxExempt(cartId: number, status: 0 | 1): Promise<ApiCart> {
  // CLI's changeValue() replaces its whole cartSummary with this response
  // (`this.cartSummary = res.result.data`) — tax/total change immediately, not just the
  // toggle's own on/off state. Discarding the body here meant the tax/total lines never
  // actually updated after switching the toggle.
  const raw = await http.post<ApiEnvelope<{ data?: ApiCart } | ApiCart>>('/admin/tax/exempt', {
    type: 'cart',
    id: cartId,
    status,
  });
  if (raw.status !== 'OK') {
    throw new Error('Failed to update tax exempt status');
  }
  const r = raw.result;
  const cart = (r as { data?: ApiCart })?.data ?? (r as ApiCart);
  if (!cart?.cart_items) throw new Error('Failed to update tax exempt status');
  return cart;
}

// ── Fulfillment: delivery/shipping charge (CLI: shipping-info/*) ─────────────────

export interface DeliveryAddress {
  shipping_first_name?: string;
  shipping_last_name?: string;
  shipping_mobile?: string;
  shipping_email?: string;
  shipping_address1: string;
  shipping_address2?: string;
  shipping_city: string;
  shipping_state: string;
  shipping_zipcode: string;
  shipping_country: string;
}

/** CLI: GET free-shipping/{token} — a server-side threshold check (cart total vs. a configured
 *  free-shipping minimum); when true CLI skips the rate lookup entirely and applies method 5. */
export async function checkFreeShipping(cartToken: number): Promise<boolean> {
  try {
    const raw = await http.get<ApiEnvelope<unknown>>(`/free-shipping/${cartToken}`);
    return raw.status === 'OK' && !!raw.result;
  } catch {
    return false;
  }
}

export interface DeliveryChargeOption {
  id?: number;
  name?: string;
  charge: number;
  is_free?: boolean;
  matched?: boolean;
  /** CLI: a truthy max_distance on a non-zone response means the address is outside the
   *  delivery area entirely — no charge is computed. */
  max_distance?: boolean;
}

/** CLI: POST delivery-charge-list — a single matched location/charge (method 2), or, when the
 *  store charges by zone, an array of selectable zones (method 2 either way; multi-distance
 *  method 3 is a separate flow set upstream at add-to-cart time, not implemented here). */
export async function getDeliveryChargeList(
  address: DeliveryAddress,
  pickupLocationId: number,
): Promise<{ chargeByZone: boolean; options: DeliveryChargeOption[] } | null> {
  try {
    const raw = await http.post<ApiEnvelope<Record<string, unknown>>>('/delivery-charge-list', {
      address,
      pickup: pickupLocationId,
    });
    if (raw.status !== 'OK') return null;
    const loc = raw.result?.location;
    if (!Array.isArray(loc) || loc.length === 0) return null;
    // This backend echoes money fields as strings (e.g. "40.00") — casting the raw array
    // straight to DeliveryChargeOption[] (as before) left `charge` a string at runtime despite
    // the type saying `number`, so `deliveryCharge.value + deliveryTax.value` downstream did
    // string concatenation instead of addition ("40.00" + 0 = "40.000"), corrupting the whole
    // order total into a garbage string once added to it.
    const options = (loc as Record<string, unknown>[]).map((o) => ({
      ...o,
      charge: Number(o.charge ?? 0) || 0,
    })) as DeliveryChargeOption[];
    return { chargeByZone: options.length > 1, options };
  } catch {
    return null;
  }
}

export interface ShippingRateOption {
  method: number; // 4=carrier, 6=standard, 7=flat
  name: string;
  charge: number;
}

/** CLI: POST shipping/rate — a keyed map of carrier categories; `standard`/`flat` are
 *  special-cased to fixed method codes, `boxes` is ignored, everything else is a list of
 *  carrier options (method 4). `weight.weight === 0` is a soft warning (missing product
 *  weight config), not a hard error — CLI still lets the cashier continue past it. */
export async function getShippingRates(
  address: DeliveryAddress,
  pickupLocationId: number,
  cartToken: number,
): Promise<{ options: ShippingRateOption[]; weightWarning?: string } | null> {
  try {
    const raw = await http.post<ApiEnvelope<Record<string, unknown>>>('/shipping/rate', {
      address,
      pickup: pickupLocationId,
      token: cartToken,
    });
    if (raw.status !== 'OK') return null;
    const data = raw.result ?? {};
    const options: ShippingRateOption[] = [];
    for (const [key, val] of Object.entries(data)) {
      if (key === 'boxes' || key === 'weight' || !val) continue;
      if (key === 'standard') {
        const v = val as Record<string, unknown>;
        options.push({ method: 6, name: 'Standard', charge: Number(v.charge ?? val) || 0 });
      } else if (key === 'flat') {
        const v = val as Record<string, unknown>;
        options.unshift({ method: 7, name: 'Flat Rate', charge: Number(v.charge ?? val) || 0 });
      } else if (Array.isArray(val)) {
        for (const opt of val as Record<string, unknown>[]) {
          options.push({ method: 4, name: String(opt.name ?? key), charge: Number(opt.charge ?? 0) });
        }
      }
    }
    const weight = data.weight as { weight?: number; message?: string } | undefined;
    return {
      options,
      weightWarning: weight && Number(weight.weight) === 0 ? weight.message : undefined,
    };
  } catch {
    return null;
  }
}

/** CLI: POST carts/delivery — persists the chosen fulfillment method + cost onto the
 *  server-side cart immediately (not held purely in local state until final order creation),
 *  which is why CLI's cart total already reflects delivery the moment this step is left. */
export async function addDeliveryCharge(payload: {
  token: number;
  shipping_method: number;
  shipping_cost: number;
  shipping_tax?: number;
  address?: DeliveryAddress;
}): Promise<ApiCart | null> {
  try {
    const raw = await http.post<ApiEnvelope<{ data?: ApiCart } | ApiCart>>('/carts/delivery', payload);
    if (raw.status !== 'OK') return null;
    const r = raw.result;
    const cart = (r as { data?: ApiCart })?.data ?? (r as ApiCart);
    return cart?.cart_items ? cart : null;
  } catch {
    return null;
  }
}

export interface DeliverySettings {
  disable_instore_pickup?: boolean;
  is_requiered?: boolean; // CLI's own field name/typo — this really means "delivery enabled"
  shipping?: boolean;
  charge_by_zone?: boolean;
}

/** CLI: GET stores/delivery-settings — governs which of the three fulfillment tabs
 *  (Pickup/Delivery/Shipping) are even shown at all (shipping.component.html): Pickup unless
 *  disable_instore_pickup, Delivery when is_requiered, Shipping when `shipping` is truthy
 *  (CLI also ORs in a store-config flat_rate flag we don't have local access to). */
export async function getDeliverySettings(): Promise<DeliverySettings | null> {
  try {
    type DR = { delivery_settings?: DeliverySettings };
    const raw = await http.get<ApiEnvelope<DR>>('/stores/delivery-settings');
    if (raw.status !== 'OK') return null;
    return raw.result?.delivery_settings ?? null;
  } catch {
    return null;
  }
}

// ── Stripe PaymentIntent (CLI: stripe-checkout-v3.component.ts initPaymentMethod()) ──────────

export interface StripeIntentResult {
  requires_action?: boolean;
  payment_intent_client_secret?: string;
  [key: string]: unknown;
}

/** CLI: POST payments/stripe-intent — this is where the charge/authorization actually happens
 *  (via Stripe's PaymentIntents API server-side); the raw response `data` gets attached
 *  as-is to the final order-creation payload's `account` field for reference, it isn't a token
 *  the /orders endpoint re-processes. */
export async function createStripeIntent(payload: {
  store_id: number;
  amount: number;
  payment_method_id: string;
  source: 'pos' | 'admin';
  capture?: boolean;
  cart_id?: number;
}): Promise<StripeIntentResult> {
  const raw = await http.post<ApiEnvelope<{ data?: StripeIntentResult; message?: string }>>(
    '/payments/stripe-intent',
    payload,
  );
  if (raw.status !== 'OK' || !raw.result?.data) {
    throw new Error(raw.result?.message ?? 'Payment failed. Please try again.');
  }
  return raw.result.data;
}

// ── Fortis (CLI: fortis-checkout.component.ts) — Commerce.js tokenizing iframe ───────────────
// CLI loads Fortis's own Commerce.js and mounts an iframe via `commerce.elements()`; this call
// is the client-token mint that iframe needs before it can render. `method_type` picks card vs
// ACH — CLI sends 'sale' for action when the gateway config's checkout_online_capture is true,
// else 'auth-only' (mirrors our own `capture` flag semantics elsewhere in this file).
export interface FortisInitResult {
  client_token: string;
  environment: string;
}

export async function initFortisGateway(payload: {
  amount: number;
  description: string;
  action: 'sale' | 'auth-only';
  method_type: 'cc' | 'ach';
  order_id?: number;
  cart_id?: number;
  customer_id?: number;
}): Promise<FortisInitResult> {
  const raw = await http.post<ApiEnvelope<{ data?: { frontend?: FortisInitResult } & Partial<FortisInitResult>; frontend?: FortisInitResult; message?: string }>>(
    '/gateway/fortis/init',
    payload,
  );
  // CLI unwraps defensively from result.data.frontend | data.frontend | frontend — the exact
  // nesting has varied across backend versions.
  const r = raw.result;
  const frontend = r?.data?.frontend ?? r?.frontend ?? (r?.data as FortisInitResult | undefined);
  if (raw.status !== 'OK' || !frontend?.client_token || !frontend?.environment) {
    throw new Error(r?.message ?? 'Failed to start Fortis checkout.');
  }
  return frontend;
}

// ── Transact (CLI: paymnet-settings.component.ts goTransactPayment()/transactInitialize()) ───
// Verified directly against CLI's real implementation (not rentmy_pos — its GatewayTransact.vue
// is an unfinished stub that just emits a fake success event with no real checkout at all).
// Like Touchnet, CLI supports Transact for BOTH a brand-new order and an existing one:
// paymnet-settings.component.ts's new-order checkout creates the order first via the normal
// addPayment() call with payment_gateway_name:'Transact', then immediately calls
// goTransactPayment('order') once a real order_id exists — same 'order' vs 'after_order' branch
// split as goTouchNetPayment(). It's a same-webview redirect (via Transact's own RapidCheckout
// SDK, see startTransactCheckoutSession()/GatewayTransact.vue), not an external browser + deep
// link — `success_url` points at this app's OWN `gateway/transact/success` API endpoint (which
// attaches the payment server-side using the sessionId, then 302s to the `return_url` it's
// given), while `cancel_url` points directly back at our own order page.
export interface TransactInitResult {
  sessionId: string;
  baseURL?: string;
  mode?: 'pay' | 'setup';
  requiresFollowupPay?: boolean;
}

export async function initTransactGateway(payload: {
  customer_id: number;
  amount: number;
  order_id: number;
  store_id: number;
  location: number;
  success_url: string;
  cancel_url: string;
  // CLI (transactInitialize()): `status: activeGateway?.status` — the card gateway's own
  // config-record status flag, sent alongside every init call. Optional since callers may not
  // always have gatewayGroups.card loaded (e.g. a stale/edge-case render) — CLI itself would just
  // send `undefined` in that case too (activeGateway?.status on a missing gateway).
  status?: number;
  // CLI: `if (showPayOrderRadio && payOrderMode === 'full') { param.pay_order_full = true; }` —
  // tells the backend to authoritatively recompute/charge the live order total server-side,
  // ignoring the `amount` field above (kept for backwards compatibility with older backends).
  pay_order_full?: boolean;
}): Promise<TransactInitResult> {
  const raw = await http.post<ApiEnvelope<TransactInitResult & { message?: string }>>('/gateway/transact/init', {
    name: 'Transact',
    is_admin: true,
    type: 'online',
    mode: 'pay',
    id: payload.order_id,
    ...payload,
  });
  if (raw.status !== 'OK' || !raw.result?.sessionId) {
    throw new Error(raw.result?.message ?? 'Failed to start Transact checkout.');
  }
  return raw.result;
}

// A customer who has already paid via Transact once has their card vaulted server-side — CLI
// (getTransactPayDetails(), paymnet-settings.component.ts:6079-6171) checks for this and, if
// found, lets them pay with that card directly (skipping RapidCheckout/startTransactCheckoutSession
// entirely) instead of always making them re-enter a new one. The whole object is kept as-is
// (not narrowed to just the display fields) because CLI echoes it back verbatim as the `account`
// field of the charge payload later — see PaymentPanel.vue's chargeAndBuildFields().
export interface TransactSavedCard {
  lastFourDigits: string;
  expirationMonth: string | number;
  expirationYear: string | number;
  [key: string]: unknown;
}

// CLI also has an email-only lookup (orders/customer/search?...) for the rare case where a
// brand-new order's customer has an email but no resolved customer id yet — not ported here,
// since every screen that calls this already has a resolved numeric customer id by the time
// payment starts (an existing customer must be picked/attached first).
export async function getTransactSavedCards(customerId: number): Promise<TransactSavedCard[]> {
  try {
    const raw = await http.get<ApiEnvelope<Record<string, unknown>>>(`/customers/${customerId}`);
    if (raw.status !== 'OK') return [];
    const data = (raw.result?.['data'] ?? raw.result) as Record<string, unknown> | undefined;
    const cards = data?.['Transact'];
    return Array.isArray(cards) ? (cards as TransactSavedCard[]) : [];
  } catch {
    return [];
  }
}

// Same vault concept as Transact above, for Fortis (CLI: getFortisDetails(),
// paymnet-settings.component.ts:4617-4689) — same `GET customers/{id}` lookup, just a different
// key (`Fortis` instead of `Transact`) and a much simpler card shape: just a masked last-4
// (`card_mask`) and an opaque `card_token` used both as the vault charge's `account` value and to
// address the delete endpoint below. No expiry is shown for these (CLI: getFortisOldNumber()
// only ever renders "****" + card_mask, nothing else).
export interface FortisSavedCard {
  card_mask: string;
  card_token: string;
  [key: string]: unknown;
}

export async function getFortisSavedCards(customerId: number): Promise<FortisSavedCard[]> {
  try {
    const raw = await http.get<ApiEnvelope<Record<string, unknown>>>(`/customers/${customerId}`);
    if (raw.status !== 'OK') return [];
    const data = (raw.result?.['data'] ?? raw.result) as Record<string, unknown> | undefined;
    const cards = data?.['Fortis'];
    return Array.isArray(cards) ? (cards as FortisSavedCard[]) : [];
  } catch {
    return [];
  }
}

// CLI: deleteFortisSavedCard() — a BE-only delete, no response body to speak of beyond a plain
// OK/error envelope; the caller (PaymentPanel.vue) does the optimistic local-list update itself.
export async function deleteFortisSavedCard(customerId: number, cardToken: string): Promise<void> {
  const raw = await http.delete<ApiEnvelope<{ message?: string }>>(`/customers/${customerId}/fortis/cards/${cardToken}`);
  if (raw.status !== 'OK') {
    throw new Error(raw.result?.message ?? 'Could not remove saved card');
  }
}

declare global {
  interface Window {
    RapidCheckout?: {
      TransactDisplayType: { redirect: string };
      startCheckoutSession: (sessionId: string, options: { displayType: string }) => void;
    };
  }
}

function loadTransactScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { resolve(); return; }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Transact checkout script.'));
    document.body.appendChild(script);
  });
}

// Shared by GatewayTransact.vue's own button (existing order) and PaymentPanel.vue's
// triggerPostOrderPayment() (brand-new order, fired right after order creation succeeds) — both
// need the exact same script-load + session-start call, just from different trigger points.
export async function startTransactCheckoutSession(
  sessionId: string,
  baseURL?: string,
  mode?: 'pay' | 'setup',
  requiresFollowupPay?: boolean,
): Promise<void> {
  const src = `${baseURL ?? 'https://commerce.cashnet.com'}/rapidcheckout/js/checkout.js`;
  await loadTransactScript(src);
  if (!window.RapidCheckout) throw new Error('Transact checkout script did not load correctly.');
  // CLI (transactInitialize(), paymnet-settings.component.ts:7305-7309) only starts the redirect
  // for `(mode==='setup' && requiresFollowupPay) || (mode==='pay' && !requiresFollowupPay)` —
  // silently no-ops otherwise. The admin "pay" flow's real backend always answers with
  // mode:'pay'/requiresFollowupPay:false, so this rarely diverges in practice, but match it
  // exactly rather than assuming: an undefined mode (e.g. an older/different backend response
  // shape) still starts the session, same as omitting both params entirely.
  if (mode != null && requiresFollowupPay != null) {
    const shouldStart = (mode === 'setup' && requiresFollowupPay) || (mode === 'pay' && !requiresFollowupPay);
    if (!shouldStart) return;
  }
  window.RapidCheckout.startCheckoutSession(sessionId, { displayType: window.RapidCheckout.TransactDisplayType.redirect });
}

// ── Touchnet (CLI: paymnet-settings.component.ts goTouchNetPayment(), globals/_classes/
// functions.ts gotoTouchnetPaymentPage()) ──────────────────────────────────────────────────────
// A same-webview redirect, same family as Transact, but simpler: no proprietary SDK, no card
// fields collected client-side at all — just POST payments/touchnet (billing info + amount +
// three return links) to get a one-time {ticket, ticket_name} pair, then submit a plain hidden
// HTML form (fields UPAY_SITE_ID/TICKET/TICKET_NAME) to the store's own configured TouchNet uPay
// URL (gatewayGroups.card.config.upayUrl/upay_site_id) — this is TouchNet's real, documented uPay
// integration pattern, not a guess. Unlike Transact, CLI supports Touchnet for BOTH a brand-new
// order (order gets created normally first with payment_gateway_name:'Touchnet', THEN this fires)
// and an existing order's Add Payment (fires directly, no order-side call first — the backend's
// own payments/touchnet/{order_id}/success/{source} callback attaches the payment once TouchNet's
// hosted page redirects back).
export interface TouchnetInitResult {
  ticket: string;
  ticket_name: string;
}

export async function initTouchnetPayment(payload: {
  order_id: number;
  amount: number;
  success_url: string;
  error_url: string;
  cancel_url: string;
  tax?: number;
  subtotal?: number;
  // Billing info — CLI's own billInfo is genuinely optional (POS explicitly sends {} when the
  // cashier skipped customer info), so every field here is too.
  first_name?: string;
  last_name?: string;
  email?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
}): Promise<TouchnetInitResult> {
  const { order_id, success_url, error_url, cancel_url, ...rest } = payload;
  const raw = await http.post<ApiEnvelope<
    { data?: TouchnetInitResult & { message?: string } } & Partial<TouchnetInitResult> & { message?: string }
  >>('/payments/touchnet', {
    ...rest,
    success_link: success_url,
    error_link: error_url,
    cancel_link: cancel_url,
  });
  // The real response nests ticket/ticket_name under `result.data`, not directly on `result`
  // (confirmed via a live network capture: {status:"OK", result:{data:{success, ticket,
  // ticket_name, ticketExpire, status}}}) — reading raw.result.ticket directly always came back
  // undefined, so every genuinely successful init was being thrown away as a failure. Falls back
  // to raw.result itself in case a different backend build ever returns it flat.
  const data = raw.result?.data ?? raw.result;
  if (raw.status !== 'OK' || !data?.ticket || !data?.ticket_name) {
    throw new Error(data?.message ?? raw.result?.message ?? 'Failed to start Touchnet checkout.');
  }
  return { ticket: data.ticket, ticket_name: data.ticket_name };
}

/** CLI: functions.ts gotoTouchnetPaymentPage() — a plain hidden HTML form POST that navigates
 *  the current page to TouchNet's hosted uPay checkout. Not an API call; a browser navigation. */
export function goToTouchnetPaymentPage(actionUrl: string, upaySiteId: string, ticket: string, ticketName: string): void {
  if (!actionUrl || !upaySiteId || !ticket || !ticketName) return;
  const form = document.createElement('form');
  form.method = 'post';
  form.action = actionUrl;
  const fields: Record<string, string> = { UPAY_SITE_ID: upaySiteId, TICKET: ticket, TICKET_NAME: ticketName };
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

// ── Card-present (swipe/tap) payments — physical terminal, not this device ─────────────────
// CLI/POS: the "terminal" is a Stripe (or Fortis/Fiserv/PAX/etc.) reader registered against a
// store location, controlled entirely server-side — the app just kicks off a collection request
// and polls for its outcome. There's no on-device Bluetooth/USB reader SDK involved here at all,
// which is what makes this reachable from a plain WebView app like this one.
//
// Endpoints and payload/response shapes below are verified against the real Angular CLI
// (cash_register/payment-gateway/service/payment.service.ts + .component.ts), not guessed from
// a secondary reference — an earlier pass of this file used endpoint names that don't exist.

/** POST stripe/terminal/collect-inputs — tells the reader to show the terms and capture a
 *  signature ("Sign & Pay on Terminal"). Resolves with the raw envelope (even a `status: 'NOK'`
 *  one) so the caller can inspect it via stripeIsInProgress(); only throws on a genuine
 *  transport/HTTP failure. The actual collection outcome comes from pollStripeSwipeStatus(). */
export async function stripeCollectInputs(payload: {
  terminal_id: number;
  amount: number;
  signature_label?: string;
  // Cart-mode (no order yet) addresses by the cart TOKEN (not the numeric cart id) under this
  // same key — CLI: `cartService.getSessionData('cartToken')`. Existing-order mode uses order_id
  // instead. The two are mutually exclusive.
  cart_id?: number;
  order_id?: number;
  force?: boolean;
}): Promise<ApiEnvelope<Record<string, unknown>>> {
  return http.post<ApiEnvelope<Record<string, unknown>>>('/stripe/terminal/collect-inputs', payload);
}

/** True when a collect-inputs response body indicates the terminal already has a collection in
 *  flight — matches CLI's isInProgressBody() exactly (both conditions, not just one). */
export function stripeIsInProgress(body: unknown): boolean {
  const b = body as Record<string, unknown> | undefined;
  const data = (b?.result as Record<string, unknown> | undefined)?.data as Record<string, unknown> | undefined;
  return !!(data && data.action_id && data.status === 'awaiting_signature');
}

export interface StripeSwipeStatus {
  payment_status: 'not_started' | 'awaiting_signature' | 'signed' | 'processing' | 'succeeded' | 'failed';
  payment_intent?: unknown;
  last4?: string | null;
  card_brand?: string | null;
}

/** POST payments/stripe-swipe-status — one poll tick. Addressed either by cart_id/order_id (the
 *  "Sign & Pay" signature flow) or by stripe_intent_id (the plain "Card Swipe" button's follow-up
 *  poll after payments/swipe returns an intent id) — CLI uses both addressing styles against this
 *  same endpoint depending on which button triggered it. */
export async function pollStripeSwipeStatus(payload: {
  terminal_id: number;
  cart_id?: number;
  order_id?: number;
  stripe_intent_id?: string;
  is_last?: boolean;
}): Promise<StripeSwipeStatus | null> {
  try {
    const raw = await http.post<ApiEnvelope<StripeSwipeStatus>>('/payments/stripe-swipe-status', payload);
    return raw.result ?? null;
  } catch {
    return null;
  }
}

/** GET stripe/terminal/{terminalId}/cancel — aborts whatever the reader is currently doing (used
 *  both for an operator-initiated Cancel and as the reset step before an automatic retry). */
export async function cancelStripeCollect(terminalId: number): Promise<void> {
  try {
    await http.get(`/stripe/terminal/${terminalId}/cancel`);
  } catch { /* best effort */ }
}

/** POST payments/swipe — the generic card-present request every swipe provider goes through
 *  first. For most providers this one response IS the final outcome; Stripe (and Valor) need a
 *  follow-up poll against payments/stripe-swipe-status using the id this returns. Returns the raw
 *  `result.data` — CLI attaches it to the order as-is (not always a plain string), and for Stripe
 *  it's `{ id: <payment_intent_id>, ... }`, the shape the follow-up poll needs. */
export async function swipeCard(payload: {
  gateway_id?: number;
  terminal_id: number;
  amount: number;
  // Cart-mode uses the real numeric cart id (from the cart summary, NOT the cart token — a
  // different value than stripeCollectInputs's cart_id); existing-order mode uses order_id.
  cart_id?: number;
  order_id?: number;
  customer_id?: number;
}): Promise<unknown> {
  const raw = await http.post<ApiEnvelope<{ data?: unknown; message?: string }> & { message?: string }>(
    '/payments/swipe',
    payload,
  );
  if (raw.status !== 'OK') {
    throw new Error((raw.result as { message?: string } | undefined)?.message ?? raw.message ?? 'Card swipe failed');
  }
  return raw.result?.data ?? null;
}

export async function createOrder(payload: CreateOrderPayload): Promise<CreateOrderResponse> {
  type OrderResult = { data?: { payment?: CreateOrderResponse }; payment?: CreateOrderResponse };
  const raw = await http.post<ApiEnvelope<OrderResult>>('/orders', payload);
  if (raw.status !== 'OK') {
    const r = raw.result as unknown as { message?: string; error?: string };
    const msg = r?.message ?? r?.error ?? 'Failed to create order';
    throw new Error(msg);
  }
  const r = raw.result;
  const inner = r.data?.payment ?? r.payment;
  if (inner) return inner;
  return raw.result as unknown as CreateOrderResponse;
}

// ── Cart-wide rental date re-edit + availability recheck (CLI: checkAvailable()) ──

// CLI persists the last-selected Exact Start Time slot's id to localStorage
// ("pos_exact_times_option") whenever a cashier picks/changes a time slot for an EST-enabled
// product (regular rental, booking, or package), and — if present — includes it as
// `exact_times_id` in the cart-wide availability check payload (cart.component.ts:567-569).
// Without this, checking availability for a cart containing an EST item can return different
// results on mobile than CLI for the same cart/dates.
const EXACT_TIMES_OPTION_KEY = 'rentmy_pos_exact_times_option';

export function setExactTimesOption(opt: { id: number; date: string; time: string | null } | null): void {
  try {
    if (opt) localStorage.setItem(EXACT_TIMES_OPTION_KEY, JSON.stringify(opt));
    else localStorage.removeItem(EXACT_TIMES_OPTION_KEY);
  } catch { /* storage unavailable — non-fatal, just skip persistence */ }
}

export function getExactTimesOptionId(): number | undefined {
  try {
    const raw = localStorage.getItem(EXACT_TIMES_OPTION_KEY);
    if (!raw) return undefined;
    return (JSON.parse(raw) as { id?: number }).id;
  } catch {
    return undefined;
  }
}

export interface CartAvailabilityResult {
  ok: boolean;
  cart?: ApiCart;
  message?: string;
  /** Cart item ids the server flagged as unavailable for the new date range. */
  unavailableIds?: number[];
}

export async function checkCartAvailability(
  token: number,
  startDate: string,
  endDate: string,
  exactTimesId?: number,
): Promise<CartAvailabilityResult> {
  try {
    const raw = await http.post<{ status: string; result: Record<string, unknown> }>(
      '/products/availability',
      {
        type: 'cart', token, start_date: startDate, end_date: endDate, source: 'pos',
        ...(exactTimesId != null ? { exact_times_id: exactTimesId } : {}),
      },
    );
    if (raw.status === 'NOK') {
      const err = raw.result?.error as { id: number }[] | undefined;
      return {
        ok: false,
        message: (raw.result?.message as string | undefined) ?? 'Some items are unavailable for these dates',
        unavailableIds: (err ?? []).map((e) => e.id),
      };
    }
    const cart = (raw.result?.data ?? raw.result) as ApiCart | undefined;
    if (!cart) return { ok: false, message: 'Failed to check availability' };
    // On success this can still carry a message (e.g. "Rental time(s) were adjusted for store
    // open hours") — cart.rent_start/rent_end are then the server-corrected values, not an echo
    // of what was sent.
    return { ok: true, cart, message: raw.result?.message as string | undefined };
  } catch {
    return { ok: false, message: 'Failed to check availability' };
  }
}

// ── Additional charges catalog (CLI: cart.component.ts getListAdditionalChargeList()) ──

export interface AdditionalChargeFee {
  type?: 'amount' | 'percentage';
  amounts: number[];
}

export interface AdditionalCharge {
  id: number;
  description: string;
  fee: AdditionalChargeFee;
  options?: string[] | null;
  quantities?: string[];
  fix_per_item_fee?: number;
  is_required?: number;
  input_custom?: string | number;
  existing?: { id?: number; config?: { user_entered?: number; selected_option?: string | number } } | null;
  // Client-side working state (mirrors CLI's mutation of the same row objects)
  is_selected?: boolean;
  inputAmt?: number | null;
  selected_option?: string | number | null;
  /** UI-only: whether the custom-amount input is currently revealed — CLI starts this collapsed
   *  behind an "Input Amount" button (onClickAmtBtn/amountAreaToggle), not shown open by default. */
  showCustomInput?: boolean;
}

// Exported so orders.ts's order-scoped catalog fetch (getOrderAdditionalChargeCatalog) can reuse
// this exact normalization instead of duplicating it.
export function normalizeAdditionalCharge(raw: Record<string, unknown>): AdditionalCharge {
  let options = raw.options as unknown;
  if (typeof options === 'string') {
    // The backend sends "" (not null) for a charge with no real options — splitting/wrapping
    // that naively produces a bogus one-item array holding a blank string, which then renders
    // as an empty <select> under every single charge regardless of whether it has options.
    const parts = options.split(';').map((s) => s.trim()).filter(Boolean);
    options = parts.length ? parts : null;
  }
  const existing = raw.existing as AdditionalCharge['existing'];
  const fee = raw.fee as AdditionalChargeFee;
  const isRequired = !!raw.is_required;
  let inputAmt = existing?.config?.user_entered ?? null;
  if (isRequired && fee?.amounts?.length >= 1) inputAmt = fee.amounts[0];
  let selectedOption = existing?.config?.selected_option ?? (Array.isArray(options) ? options[0] : null);
  if (Number(raw.fix_per_item_fee) === 1 && selectedOption == null) selectedOption = 1;
  return {
    ...(raw as unknown as AdditionalCharge),
    options: options as string[] | null,
    inputAmt,
    selected_option: selectedOption,
    is_selected: !!raw.is_selected || isRequired,
  };
}

export async function getAdditionalChargeCatalog(
  cartToken: number,
  page: number,
  limit: number,
  search?: string,
): Promise<{ data: AdditionalCharge[]; total: number }> {
  try {
    const q = new URLSearchParams({
      type: 'active',
      cart_token: String(cartToken),
      page_no: String(page),
      limit: String(limit),
    });
    if (search) q.set('search', search);
    const raw = await http.get<{ status: string; result: { data: Record<string, unknown>[]; total: number } }>(
      `/settings/orders/additional-charges?${q}`,
    );
    if (raw.status !== 'OK') return { data: [], total: 0 };
    return {
      data: (raw.result.data ?? []).map(normalizeAdditionalCharge),
      total: raw.result.total ?? 0,
    };
  } catch {
    return { data: [], total: 0 };
  }
}

export interface AdditionalChargeSubmitItem {
  id: number;
  value: number | null;
  is_selected: boolean;
  order_additional_charge_id: number | null;
  selected_option: string | number | null;
}

// CLI's own response here isn't a cart object — it just re-fetches the cart afterward
// (loadCart()), so this only reports success/failure; call getCart() after to refresh.
export async function saveAdditionalCharges(
  cartToken: number,
  charges: AdditionalChargeSubmitItem[],
): Promise<boolean> {
  try {
    const raw = await http.post<{ status: string }>('/orders/additional-charges/create', {
      order: null,
      cart_token: cartToken,
      additional_charges: charges,
    });
    return raw.status === 'OK';
  } catch {
    return false;
  }
}
